import * as React from "react";
import { Helmet } from "react-helmet";
import { Block } from "kanopi-components/Block";
import { BrandLogo } from "kw/components/BrandLogo/BrandLogo";
import { Button } from "kanopi-components/Button";
import { CardBody } from "kanopi-components/Card";
import { DisplayMedium, ParagraphMedium } from "kanopi-components/typography";
import { Form } from "kanopi-components/Form";
import { FormControl, Input } from "kanopi-components/Input";
import { getSubdomain, handleErrorDisplay } from "kw/util/utils";
import { parseQueryString } from "kanopi-components/utils/browser";
import { PortalRow } from "kanopi-components/PortalRow";
import { useAuthenticate } from "../hooks/useAuthenticate";
import { useLoginForm } from "../hooks/useLoginForm";
import { useNavigate } from "@reach/router";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { useGate } from "statsig-react";
import { Icon } from "kanopi-components/icons/Icon";
import { ParagraphSmall } from "baseui/typography";

export function LoginPage({
  initialValues = {},
  nextUrl,
}: {
  initialValues?: { email?: string; password?: string };
  nextUrl: string;
}) {
  const { value: shouldShowAzureLoginButton } = useGate("k-166-admin-sso-config");

  const navigate = useNavigate();
  const { fieldProps, submit, loading: formLoading } = useLoginForm({ initialValues });
  const params = parseQueryString({ search: location?.search });
  const { authenticate: refreshUser, loading: refreshUserInProgress } = useAuthenticate(
    {
      login: true,
      now: false,
    }
  );

  const handleSubmit = async () => {
    try {
      await submit();
      navigate(params?.nextUrl || nextUrl, { replace: true });
    } catch (exc) {
      handleErrorDisplay(exc);
    }
  };

  const [css, theme] = useStyletron();
  const handleForgotPassword = () => {
    navigate("/password-reset/request");
  };

  React.useEffect(() => {
    if (params?.hijacked) {
      refreshUser();
    }
  }, [params?.hijacked]);

  const subdomain = getSubdomain();

  return (
    <>
      <Helmet>
        <title>Log In - {subdomain}</title>
      </Helmet>
      <PortalRow>
        <Block display="flex" justifyContent="center">
          <BrandLogo />
        </Block>
      </PortalRow>
      <Block
        backgroundColor={theme.colors.backgroundSecondary}
        borderRadius="roundedSurfaceRadius"
        maxWidth="400px"
        marginLeft={["scale700", "scale700", "auto"]}
        marginRight={["scale700", "scale700", "auto"]}
        marginTop="auto"
        marginBottom="auto"
      >
        <Form>
          <CardBody
            display="grid"
            gridGap="scale1200"
            gridTemplateColumns={["1fr"]}
            justifyItems="center"
            paddingTop="scale1200"
            paddingBottom="scale1200"
          >
            <Block>
              <DisplayMedium>Log in</DisplayMedium>
              <ParagraphMedium>
                Log in to your account to manage your insurance, make a claim and update
                your details.
              </ParagraphMedium>
              {params.error && params.description ? (
                <ParagraphSmall color={theme.colors.jumpRed400}>
                  {params.description}
                </ParagraphSmall>
              ) : null}
            </Block>
            <Block width="100%">
              <FormControl label="Your email address">
                <Input {...fieldProps.email} />
              </FormControl>
            </Block>
            <Block width="100%">
              <FormControl label="Your password">
                <Input type="password" {...fieldProps.password} />
              </FormControl>
            </Block>
            <Block display="grid" gridGap="scale800">
              <Button
                type="submit"
                kind="primary"
                wide
                onClick={handleSubmit}
                isLoading={formLoading || refreshUserInProgress}
              >
                Login
              </Button>
              {shouldShowAzureLoginButton ? (
                <>
                  <div
                    className={css({
                      position: "relative",
                      width: "100%",
                      textAlign: "center",
                    })}
                  >
                    <div
                      className={css({
                        width: "100%",
                        position: "absolute",
                        height: "1px",
                        backgroundColor: theme.colors.borderOpaque,
                        display: "block",
                        top: "50%",
                        left: 0,
                        transform: "translateY(-50%)",
                      })}
                    />
                    <ParagraphSmall
                      overrides={{
                        Block: {
                          style: {
                            padding: "0 16px",
                            backgroundColor: theme.colors.backgroundSecondary,
                            position: "relative",
                            zIndex: 1,
                            display: "inline",
                          },
                        },
                      }}
                    >
                      or
                    </ParagraphSmall>
                  </div>

                  <Button
                    type="submit"
                    kind={Button.KIND.secondary}
                    wide
                    startEnhancer={<Icon icon="microsoftAzureLogo" />}
                    href="/user-sso/login?type=SamlAuthenticator"
                  >
                    Sign in with Azure
                  </Button>
                </>
              ) : null}

              <Button
                type="button"
                kind={Button.KIND.tertiary}
                size={Button.SIZE.mini}
                onClick={handleForgotPassword}
              >
                Forgot your password?
              </Button>
            </Block>
          </CardBody>
        </Form>
      </Block>
    </>
  );
}
